import React, { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { ListChecks, BellRing, CalendarClock, Loader2, Check } from "lucide-react";
import { translateFr as tfr } from "@/i18n";

const TYPE_META = {
  reminder: { label: tfr("Relance"), Icon: BellRing, cls: "text-athlynx bg-athlynx/10 border-athlynx/30" },
  event: { label: "Agenda", Icon: CalendarClock, cls: "text-blue-500 bg-blue-500/10 border-blue-500/30" },
};
const STATUS_META = {
  created: { label: tfr("Créé"), cls: "text-emerald-500 bg-emerald-500/10 border-emerald-500/30" },
  duplicate: { label: tfr("Doublon ignoré"), cls: "text-amber-500 bg-amber-500/10 border-amber-500/30" },
  rejected: { label: tfr("Rejeté"), cls: "text-red-500 bg-red-500/10 border-red-500/30" },
};

export const ActionPlanPanel = ({ plan }) => {
  const [items, setItems] = useState(() => plan.map((a) => ({ ...a, checked: true })));
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState(null);
  const upd = (i, patch) => setItems((xs) => xs.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const selected = items.filter((x) => x.checked);

  const confirm = async () => {
    if (!selected.length || busy) return;
    setBusy(true);
    try {
      const r = await api.post("/intelligence/action-plan/confirm", {
        actions: selected.map(({ checked, ...a }) => a),
      });
      setResults(r.data);
      const { created, skipped } = r.data;
      toast.success(`${created} ${created > 1 ? tfr("actions créées") : tfr("action créée")}${skipped ? ` · ${skipped} ${skipped > 1 ? tfr("ignorées") : tfr("ignorée")}` : ""}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || tfr("Impossible de créer le plan d'action."));
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-xl border border-border bg-secondary/20 p-3.5" data-testid="action-plan-panel">
      <div className="flex items-center gap-2">
        <ListChecks className="h-4 w-4 text-athlynx" aria-hidden="true" />
        <p className="text-sm font-semibold">{tfr("Plan d'action proposé")}</p>
      </div>
      <p className="text-[11px] text-muted-foreground mt-0.5">
        {tfr("Vérifiez, ajustez puis confirmez — rien n'est créé sans votre validation.")}
      </p>

      {results ? (
        <div className="mt-3 space-y-1.5" data-testid="action-plan-results">
          {results.results.map((r, i) => {
            const s = STATUS_META[r.status] || STATUS_META.rejected;
            return (
              <div key={i} className="flex items-center gap-2 rounded-lg border border-border bg-card px-2.5 py-2" data-testid={`action-plan-result-${i}`}>
                <p className="text-xs font-medium flex-1 min-w-0 truncate">{r.title}</p>
                <span className={cn("text-[10px] font-semibold rounded-full border px-2 py-0.5 shrink-0", s.cls)}>{s.label}</span>
              </div>
            );
          })}
          <p className="text-[11px] text-muted-foreground pt-1">
            {results.created} {results.created > 1 ? tfr("actions ajoutées à vos relances et à votre agenda.") : tfr("action ajoutée à vos relances et à votre agenda.")}
          </p>
        </div>
      ) : (
        <>
          <div className="mt-3 space-y-2">
            {items.map((a, i) => {
              const t = TYPE_META[a.type] || TYPE_META.reminder;
              return (
                <div key={i} className={cn("rounded-lg border border-border bg-card p-2.5 transition-opacity", !a.checked && "opacity-50")}
                  data-testid={`action-plan-item-${i}`}>
                  <div className="flex items-center gap-2.5">
                    <input type="checkbox" checked={a.checked} onChange={(e) => upd(i, { checked: e.target.checked })}
                      className="h-4 w-4 shrink-0 accent-athlynx cursor-pointer" aria-label={`Inclure « ${a.title} »`}
                      data-testid={`action-plan-check-${i}`} />
                    <Input value={a.title} onChange={(e) => upd(i, { title: e.target.value })} disabled={!a.checked}
                      className="h-8 text-xs flex-1 min-w-0" data-testid={`action-plan-title-${i}`} aria-label={tfr("Titre de l'action")} />
                  </div>
                  <div className="flex flex-wrap items-center gap-2 mt-2 pl-6">
                    <span className={cn("text-[10px] font-semibold rounded-full border px-2 py-0.5 inline-flex items-center gap-1", t.cls)}>
                      <t.Icon className="h-3 w-3" aria-hidden="true" /> {t.label}
                    </span>
                    {a.player_name && <span className="text-[11px] text-muted-foreground">{a.player_name}</span>}
                    <input type="date" value={a.due_date} onChange={(e) => upd(i, { due_date: e.target.value })} disabled={!a.checked}
                      className="h-7 text-[11px] rounded-md border border-border bg-transparent px-2 text-foreground ml-auto"
                      data-testid={`action-plan-date-${i}`} aria-label={tfr("Date d'échéance")} />
                  </div>
                </div>
              );
            })}
          </div>
          <div className="flex items-center justify-between mt-3">
            <p className="text-[11px] text-muted-foreground">{selected.length} {selected.length > 1 ? tfr("actions sélectionnées") : tfr("action sélectionnée")}</p>
            <Button size="sm" onClick={confirm} disabled={busy || !selected.length}
              className="h-8 gap-1.5 bg-athlynx hover:bg-athlynx/90 text-white" data-testid="action-plan-confirm">
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
              {tfr("Confirmer le plan d'action")}
            </Button>
          </div>
        </>
      )}
    </div>
  );
};
